/**
 * @voltxampere/web — Engine pre-filter for damaged components (Sprint 36-4)
 *
 * Engine zero-diff invariant guard. Damaged components are removed
 * (or substituted) at the apps/web layer BEFORE the circuit reaches
 * `solveTransient` / `solveAC`. The engine never learns about
 * "damage" as a concept — it just sees a smaller / structurally-
 * different netlist.
 *
 * Three damage outcomes per type:
 *
 *   - LED open mode:        component dropped (acts as open circuit)
 *   - LED short mode:       replaced with low-R resistor stamp
 *   - Capacitor / resistor: dropped (open circuit fallback)
 *   - Generic (BJT/MOSFET): dropped (open circuit fallback)
 *
 * The LED short-mode substitution preserves the original net
 * connections + position so the canvas overlay still draws the
 * post-explosion charred body in the right place. The engine sees a
 * 0.01 Ω resistor; the user sees the LED hulk + a fully-conducting
 * path.
 *
 * Pattern: Sprint 23 disabled-component schema-filter. Engine sees
 * subset, filter step lives at apps/web boundary.
 *
 * Engine zero-diff invariant honored — this module imports only
 * `Circuit` / `ComponentInstance` types from `@voltxampere/schema`,
 * nothing from `@voltxampere/engine`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit, ComponentInstance } from "@voltxampere/schema";

import { damageMap, type ComponentDamage } from "../store/damage-state.js";

/**
 * Substitution resistance for LED short mode (Ohms). Low enough to
 * act as effectively short-circuit at typical LED supply voltages
 * but non-zero so the MNA matrix stays well-conditioned (Sprint 7
 * minimum-resistance pattern). Picked so that 5V across a single
 * 0.01 Ω resistor draws 500 A in the engine — a clear "wow this
 * shorted" visual on the inspector.
 */
export const LED_SHORT_RESISTANCE_OHMS = 0.01;

/**
 * Filter result. `removed` and `substituted` are diagnostic — the
 * UI surfaces them via inspector / restart-toast (Sprint 36-5).
 */
export interface CircuitFilterResult {
  /** Filtered circuit (passes to engine). */
  readonly circuit: Circuit;
  /** Component IDs dropped from the circuit (open-circuit damage). */
  readonly removedIds: readonly string[];
  /** Component IDs whose stamp was substituted (LED short → resistor). */
  readonly substitutedIds: readonly string[];
}

/**
 * Filter the circuit by removing / substituting damaged components.
 *
 * Reads the current `damageMap` signal value (snapshotted at call
 * time). Caller is responsible for calling this from a reactive
 * effect or from each engine entry point (transient / AC) — the
 * filter does not subscribe.
 */
export function filterDamagedCircuit(circuit: Circuit): CircuitFilterResult {
  const dmgMap = damageMap.value;
  // Fast path — no damage, return original circuit unchanged.
  if (!hasAnyPermanentDamage(dmgMap)) {
    return { circuit, removedIds: [], substitutedIds: [] };
  }

  const removed: string[] = [];
  const substituted: string[] = [];
  const newComponents: ComponentInstance[] = [];

  for (const c of circuit.components) {
    const dmg = dmgMap.get(c.id);
    if (dmg?.status !== "damaged-permanent") {
      newComponents.push(c);
      continue;
    }
    const action = damageActionFor(dmg, c.type);
    if (action === "remove") {
      removed.push(c.id);
      continue;
    }
    // Only "short-resistor" remains.
    newComponents.push(substituteAsLowR(c));
    substituted.push(c.id);
  }

  return {
    circuit: { ...circuit, components: newComponents },
    removedIds: removed,
    substitutedIds: substituted,
  };
}

/**
 * Pre-flight predicate: does the damage map contain any permanently
 * damaged entry? Used to skip the filter pass entirely on healthy
 * circuits (the common case).
 */
function hasAnyPermanentDamage(map: ReadonlyMap<string, ComponentDamage>): boolean {
  for (const [, d] of map) {
    if (d.status === "damaged-permanent") return true;
  }
  return false;
}

type DamageAction = "remove" | "short-resistor";

/**
 * Resolve the damage action for a component given its damage record
 * + original schema type.
 */
function damageActionFor(dmg: ComponentDamage, componentType: string): DamageAction {
  if (componentType === "led" && dmg.mode === "led-pop") {
    return dmg.ledFailureMode === "short" ? "short-resistor" : "remove";
  }
  // All other damaged components → open circuit (remove from netlist).
  return "remove";
}

/**
 * Replace an LED with a low-R resistor stamp (LED short failure
 * mode). Preserves id, position, terminals — the engine sees a
 * resistor, the canvas keeps its visual position so the explosion
 * overlay can keep drawing the charred body there.
 *
 * Resistor stamp's terminal layout differs from LED's (resistor =
 * `a`/`b`, LED = `anode`/`cathode`). We rename terminals to keep the
 * schema-level Zod validation happy.
 */
function substituteAsLowR(led: ComponentInstance): ComponentInstance {
  const terminals = led.terminals.map((t, idx) => ({
    ...t,
    terminalId: idx === 0 ? "a" : "b",
  }));
  return {
    ...led,
    type: "resistor",
    params: { resistance: LED_SHORT_RESISTANCE_OHMS },
    terminals,
  };
}
